import { useState, type ReactNode } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowDown01Icon,
  ArrowUp01Icon,
  BubbleChatDoneIcon,
  Cancel01Icon,
  InboxIcon,
  Layout04Icon,
  PictureInPictureOnIcon,
  SidebarRightIcon
} from "@hugeicons-pro/core-stroke-rounded";
import {
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Show
} from "shared/components";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "shared/components/Popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import { MESSAGE_POPUP_MENU_Z_CLASS } from "softphone/stacking";
import { cx } from "shared/utils/classnames";
import {
  NO_REPLY_NEEDED_LABEL,
  NO_REPLY_NEEDED_TOOLTIP,
  useNoReplyNeeded
} from "views/inbox/hooks/useNoReplyNeeded";
import { applicantName } from "../applicantName";
import type { MessagePopupLayout } from "../constants";
import type { Applicant } from "types/application";

const TOOLTIP_CLASS = "max-w-[260px] rounded-lg border p-3 text-sm text-white";

/**
 * The design labels the header icons with a tooltip. They cannot use
 * IconButton's own `tooltip` prop: the layout one is a `PopoverTrigger asChild`
 * child, and that would clone IconButton's tooltip wrapper instead of the
 * button. Wrapping the trigger in a span keeps both behaviours intact, and the
 * explicit z-class is what lifts the tooltip above the pop-up itself.
 */
const HeaderTooltip = ({
  label,
  children
}: {
  label: string;
  children: ReactNode;
}) => (
  <Tooltip placement="bottom">
    <TooltipTrigger asChild>
      <span className="inline-flex">{children}</span>
    </TooltipTrigger>
    <TooltipContent className={cx(TOOLTIP_CLASS, MESSAGE_POPUP_MENU_Z_CLASS)}>
      {label}
    </TooltipContent>
  </Tooltip>
);

// CRM-1931: the pop-up's icon version of the Inbox header's No reply needed.
const NoReplyNeededHeaderButton = ({
  conversationId,
  size
}: {
  conversationId: string;
  size: IconButtonSize;
}) => {
  const { available, isPending, markNoReplyNeeded } =
    useNoReplyNeeded(conversationId);

  return (
    <Show if={available}>
      <HeaderTooltip label={NO_REPLY_NEEDED_TOOLTIP}>
        <IconButton
          HugeIcon={BubbleChatDoneIcon}
          variant={IconButtonVariant.TEXT}
          size={size}
          disabled={isPending}
          onClick={markNoReplyNeeded}
          aria-label={NO_REPLY_NEEDED_LABEL}
          className="!text-content-1000"
        />
      </HeaderTooltip>
    </Show>
  );
};

const MENU_CARD_CLASS =
  "min-w-[232px] overflow-hidden rounded-lg border border-alpha-100 bg-white shadow-lg";
const MENU_ITEM_CLASS =
  "flex w-full items-center gap-3 border-b border-alpha-100 px-4 py-3 text-left text-sm text-content-1000 last:border-b-0 hover:bg-alpha-40";

interface MessagePopupHeaderProps {
  contactName: string;
  /** Unset while the pop-up shows a draft (no conversation yet). */
  conversationId?: string;
  layout: MessagePopupLayout;
  applicants: Applicant[];
  selectedApplicantId?: number;
  onSelectApplicant: (applicantId: number) => void;
  onLayoutChange: (layout: MessagePopupLayout) => void;
  onGoToInbox: () => void;
  onClose: () => void;
}

export const MessagePopupHeader = ({
  contactName,
  conversationId,
  layout,
  applicants,
  selectedApplicantId,
  onSelectApplicant,
  onLayoutChange,
  onGoToInbox,
  onClose
}: MessagePopupHeaderProps) => {
  const [applicantsOpen, setApplicantsOpen] = useState(false);
  const [layoutOpen, setLayoutOpen] = useState(false);

  // A dropdown only makes sense when there is someone to switch to; a
  // single-applicant application shows a plain name (design feedback 2026-09-02).
  const hasApplicantSwitcher = applicants.length > 1;
  const isDrawer = layout === "drawer";
  // Body/Bold on the floating card, H4/Bold in the drawer.
  const nameClass = isDrawer
    ? "text-xl leading-8 font-bold text-content-1000"
    : "text-base font-bold text-content-1000";
  const chevronSize = isDrawer ? 24 : 20;
  const iconSize = isDrawer ? IconButtonSize.LG : IconButtonSize.SM;

  return (
    <div
      className={cx(
        "flex shrink-0 items-center justify-between gap-2 border-b border-alpha-100 bg-white",
        // The design's two headers: 57px/20px gutters on the floating card,
        // 48px/24px in the drawer (was 64 before the 2152:* design wave).
        isDrawer ? "h-12 px-6" : "h-[57px] px-5"
      )}
    >
      {hasApplicantSwitcher ? (
        <Popover
          open={applicantsOpen}
          onOpenChange={setApplicantsOpen}
          placement="bottom-start"
          offsetValue={4}
        >
          <PopoverTrigger asChild onClick={() => setApplicantsOpen(true)}>
            <button
              type="button"
              className={cx("flex min-w-0 items-center gap-2", nameClass)}
            >
              <span className="truncate">{contactName}</span>
              <HugeiconsIcon
                icon={applicantsOpen ? ArrowUp01Icon : ArrowDown01Icon}
                size={chevronSize}
              />
            </button>
          </PopoverTrigger>
          <PopoverContent
            className={cx(MENU_CARD_CLASS, MESSAGE_POPUP_MENU_Z_CLASS)}
          >
            {applicants.map((applicant) => (
              <button
                key={applicant.id}
                type="button"
                className={cx(
                  MENU_ITEM_CLASS,
                  applicant.id === selectedApplicantId ? "bg-alpha-40" : ""
                )}
                onClick={() => {
                  onSelectApplicant(applicant.id);
                  setApplicantsOpen(false);
                }}
              >
                {applicantName(applicant) || applicant.email}
              </button>
            ))}
          </PopoverContent>
        </Popover>
      ) : (
        <p className={cx("truncate", nameClass)}>{contactName}</p>
      )}

      <div
        className={cx(
          "flex shrink-0 items-center",
          isDrawer ? "gap-4" : "gap-3"
        )}
      >
        {conversationId ? (
          <NoReplyNeededHeaderButton
            conversationId={conversationId}
            size={iconSize}
          />
        ) : null}
        <Popover
          open={layoutOpen}
          onOpenChange={setLayoutOpen}
          placement="bottom-end"
          offsetValue={4}
        >
          <HeaderTooltip label="Change layout">
            <PopoverTrigger asChild onClick={() => setLayoutOpen(true)}>
              <IconButton
                HugeIcon={Layout04Icon}
                variant={IconButtonVariant.TEXT}
                size={iconSize}
                aria-label="Change layout"
                className="!text-content-1000"
              />
            </PopoverTrigger>
          </HeaderTooltip>
          <PopoverContent
            className={cx(MENU_CARD_CLASS, MESSAGE_POPUP_MENU_Z_CLASS)}
          >
            <p className="border-b border-alpha-100 px-4 py-2.5 text-sm font-bold text-content-1000">
              Switch to
            </p>
            <button
              type="button"
              className={cx(
                MENU_ITEM_CLASS,
                layout === "floating" ? "bg-alpha-40" : ""
              )}
              onClick={() => {
                onLayoutChange("floating");
                setLayoutOpen(false);
              }}
            >
              <HugeiconsIcon icon={PictureInPictureOnIcon} size={20} />
              Floating
            </button>
            <button
              type="button"
              className={cx(
                MENU_ITEM_CLASS,
                layout === "drawer" ? "bg-alpha-40" : ""
              )}
              onClick={() => {
                onLayoutChange("drawer");
                setLayoutOpen(false);
              }}
            >
              <HugeiconsIcon icon={SidebarRightIcon} size={20} />
              Drawer
            </button>
            <button
              type="button"
              className={MENU_ITEM_CLASS}
              onClick={() => {
                setLayoutOpen(false);
                onGoToInbox();
              }}
            >
              <HugeiconsIcon icon={InboxIcon} size={20} />
              Go to inbox
            </button>
          </PopoverContent>
        </Popover>

        <HeaderTooltip label="Close">
          <IconButton
            HugeIcon={Cancel01Icon}
            variant={IconButtonVariant.TEXT}
            size={iconSize}
            onClick={onClose}
            aria-label="Close conversation"
            className="!text-content-1000"
          />
        </HeaderTooltip>
      </div>
    </div>
  );
};
